import { defineStore } from 'pinia'
import { ref } from 'vue'
import { isQuoteLivePriceOutOfBand } from '@/config/quote-price-bands'
import { subscribeQuotes, type QuoteTick } from '@/utils/quotes-ws'

const FLASH_MS = 480
const flashTimers = new Map<string, ReturnType<typeof setTimeout>>()

/** 展示用主价格：实物优先不带票回购，其它=最新价 */
export function tickDisplayPrice(t: QuoteTick): string {
  if (t.quoteMode === 'buyback_sale') {
    if (t.buybackExTax && t.buybackExTax !== '--') return t.buybackExTax
    if (t.buyback !== '--') return t.buyback
  }
  return t.price
}

function isDirtyTick(t: QuoteTick): boolean {
  const p = parseFloat(tickDisplayPrice(t))
  if (!Number.isFinite(p) || p <= 0) return true
  return isQuoteLivePriceOutOfBand(t.symbol, p)
}

/**
 * 全局实时行情（WebSocket 单例）
 * - App 前台期间保持连接，切 Tab 不断线
 * - 各页面 watch version 或读 tickBySymbol 即可刷新 UI
 */
export const useQuotesRealtimeStore = defineStore('quotesRealtime', () => {
  const tickBySymbol = ref<Record<string, QuoteTick>>({})
  const flashBySymbol = ref<Record<string, 'up' | 'down'>>({})
  /** 每批 tick 递增，供页面 watch 触发局部刷新 */
  const version = ref(0)
  const connected = ref(false)

  let unsubscribe: (() => void) | null = null

  function scheduleFlashClear(symbol: string): void {
    const prev = flashTimers.get(symbol)
    if (prev) clearTimeout(prev)
    flashTimers.set(
      symbol,
      setTimeout(() => {
        const next = { ...flashBySymbol.value }
        delete next[symbol]
        flashBySymbol.value = next
        flashTimers.delete(symbol)
      }, FLASH_MS),
    )
  }

  function ingest(items: QuoteTick[]): void {
    if (!items.length) return
    const next = { ...tickBySymbol.value }
    const flash = { ...flashBySymbol.value }
    for (const t of items) {
      // 单项 shape 守卫：坏帧（null / 缺 symbol）不得抛错打断整批后续 tick（High#8）
      if (!t || typeof t.symbol !== 'string' || !t.symbol) continue
      // 拒收错量纲脏 tick（如白银 12.x 伦敦银残段），避免盖住正确 HTTP 详情价
      if (isDirtyTick(t)) continue
      const prev = next[t.symbol]
      const newP = tickDisplayPrice(t)
      const oldP = prev ? tickDisplayPrice(prev) : null
      if (oldP && newP !== oldP && newP !== '--' && oldP !== '--') {
        const a = parseFloat(newP)
        const b = parseFloat(oldP)
        if (Number.isFinite(a) && Number.isFinite(b) && a !== b) {
          flash[t.symbol] = a > b ? 'up' : 'down'
          scheduleFlashClear(t.symbol)
        }
      }
      next[t.symbol] = t
    }
    tickBySymbol.value = next
    flashBySymbol.value = flash
    version.value += 1
  }

  function connect(): void {
    if (unsubscribe) return
    connected.value = true
    unsubscribe = subscribeQuotes(ingest)
  }

  function disconnect(): void {
    unsubscribe?.()
    unsubscribe = null
    connected.value = false
    // 断线同时清理 flash 定时器：避免 onHide 后仍改 flashBySymbol（Medium#17）
    flashTimers.forEach((t) => clearTimeout(t))
    flashTimers.clear()
    flashBySymbol.value = {}
  }

  function getTick(symbol: string): QuoteTick | undefined {
    return tickBySymbol.value[symbol]
  }

  function flashOf(symbol: string): 'up' | 'down' | '' {
    return flashBySymbol.value[symbol] ?? ''
  }

  return {
    tickBySymbol,
    flashBySymbol,
    version,
    connected,
    connect,
    disconnect,
    getTick,
    flashOf,
    tickDisplayPrice,
  }
})
